import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { venposService } from '../../../services/venposService';
import PaymentSenseConnectE from '../../common/PaymentSenseConnectE';
import config from '../../../config/environment';

// ─── Constants ───────────────────────────────────────────────────────────────
const LOCATION_ID = '978';
const PAGE_ID = '86677b9b-9000-468d-b1de-0191b38fc7c4';
const VENPOS_API_BASE = 'https://online1.venpos.net/VenposJourneyAPI';

const STEP_PRODUCTS     = 1;
const STEP_DETAILS      = 2;
const STEP_PAYMENT      = 3;
const STEP_CONFIRMATION = 4;

const TITLES = [
  { value: 1, label: 'Mr' },
  { value: 2, label: 'Mrs' },
  { value: 3, label: 'Miss' },
  { value: 4, label: 'Ms' },
  { value: 5, label: 'Dr' },
  { value: 6, label: 'Prof' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatPrice = (p) => `£${Number(p || 0).toFixed(2)}`;

const formatVisitDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const parseUrlDate = (raw) => {
  if (!raw) return null;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? raw : null;
};

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; }`;
const spin   = keyframes`to { transform: rotate(360deg); }`;

// ─── Styled Components ────────────────────────────────────────────────────────
const Wrapper = styled.div`
  min-height: 100vh;
  background: #f9f7f4;
  font-family: ${p => p.theme?.fonts?.body || 'Georgia, serif'};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const Inner = styled.div`
  max-width: 780px;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
  animation: ${fadeIn} 0.4s ease both;
`;

const HeroStrip = styled.div`
  background: linear-gradient(135deg, #1a2744 0%, #2c3e6b 100%);
  padding: 2.5rem 1.25rem 2rem;
  text-align: center;
  color: #fff;
`;

const HeroEyebrow = styled.p`
  font-size: 0.78rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0 0 0.5rem;
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.6rem, 4vw, 2.4rem);
  font-weight: 400;
  color: #fff;
  margin: 0 0 0.5rem;
  line-height: 1.2;
`;

const HeroSub = styled.p`
  color: rgba(255,255,255,0.72);
  font-size: 1rem;
  margin: 0;
`;

const DateBadge = styled.div`
  display: inline-block;
  margin-top: 1rem;
  background: rgba(201,169,110,0.18);
  border: 1px solid rgba(201,169,110,0.4);
  border-radius: 20px;
  padding: 0.35rem 1rem;
  font-size: 0.88rem;
  color: #c9a96e;
`;

// Progress bar
const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 1.5rem 1.25rem 0;
  max-width: 780px;
  margin: 0 auto;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  flex: 1;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 16px;
    left: calc(50% + 16px);
    right: calc(-50% + 16px);
    height: 2px;
    background: ${p => p.active || p.done ? '#c9a96e' : '#d9d2c8'};
    transition: background 0.3s;
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  border: 2px solid ${p => p.done ? '#c9a96e' : p.active ? '#1a2744' : '#d9d2c8'};
  background: ${p => p.done ? '#c9a96e' : p.active ? '#1a2744' : '#fff'};
  color: ${p => p.done || p.active ? '#fff' : '#999'};
  transition: all 0.3s;
  position: relative;
  z-index: 1;
`;

const StepLabel = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${p => p.active ? '#1a2744' : p.done ? '#c9a96e' : '#999'};
  font-weight: ${p => p.active ? 700 : 400};
  white-space: nowrap;
`;

// Card
const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e2d9;
  box-shadow: 0 2px 16px rgba(26,39,68,0.06);
  padding: 2rem;
  margin-top: 1.75rem;

  @media (max-width: 480px) { padding: 1.25rem; }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 400;
  color: #1a2744;
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0ebe2;
`;

// Product row
const ProductRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid #f0ebe2;

  &:last-child { border-bottom: none; }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.p`
  font-size: 1.05rem;
  font-weight: 600;
  color: #1a2744;
  margin: 0 0 0.2rem;
`;

const ProductDesc = styled.p`
  font-size: 0.88rem;
  color: #666;
  margin: 0 0 0.35rem;
`;

const ProductPrice = styled.p`
  font-size: 1rem;
  color: #c9a96e;
  font-weight: 600;
  margin: 0;
`;

const QtyControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const QtyBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${p => p.disabled ? '#e0d9d0' : '#1a2744'};
  background: ${p => p.disabled ? '#f5f2ee' : '#1a2744'};
  color: ${p => p.disabled ? '#bbb' : '#fff'};
  font-size: 1.2rem;
  line-height: 1;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2c3e6b;
    border-color: #2c3e6b;
  }
`;

const QtyValue = styled.span`
  min-width: 32px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a2744;
`;

// Form
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  ${p => p.full && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: 0.82rem;
  font-weight: 600;
  color: #1a2744;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Input = styled.input`
  padding: 0.7rem 0.9rem;
  border: 1.5px solid ${p => p.error ? '#c0392b' : '#d4cdc3'};
  border-radius: 8px;
  font-size: 1rem;
  color: #1a2744;
  background: #fff;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1a2744;
    box-shadow: 0 0 0 3px rgba(26,39,68,0.08);
  }
`;

const Select = styled.select`
  padding: 0.7rem 0.9rem;
  border: 1.5px solid ${p => p.error ? '#c0392b' : '#d4cdc3'};
  border-radius: 8px;
  font-size: 1rem;
  color: #1a2744;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #1a2744;
    box-shadow: 0 0 0 3px rgba(26,39,68,0.08);
  }
`;

const FieldError = styled.p`
  font-size: 0.8rem;
  color: #c0392b;
  margin: 0;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #444;
  line-height: 1.5;
  grid-column: 1 / -1;
  margin-top: 0.25rem;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 2px;
    cursor: pointer;
    accent-color: #1a2744;
  }
`;

// Order summary
const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.95rem;
  color: ${p => p.total ? '#1a2744' : '#444'};
  font-weight: ${p => p.total ? 700 : 400};
  border-top: ${p => p.total ? '1px solid #e8e2d9' : 'none'};
  margin-top: ${p => p.total ? '0.5rem' : '0'};
  padding-top: ${p => p.total ? '0.85rem' : '0.5rem'};
`;

// Discount code row
const DiscountRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 1.25rem;
`;

const DiscountInput = styled.input`
  flex: 1;
  padding: 0.7rem 0.9rem;
  border: 1.5px solid #d4cdc3;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1a2744;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1a2744;
    box-shadow: 0 0 0 3px rgba(26,39,68,0.08);
  }

  &::placeholder {
    text-transform: none;
    letter-spacing: 0;
    color: #aaa;
  }
`;

const ApplyBtn = styled.button`
  padding: 0.7rem 1.25rem;
  background: #fff;
  color: #1a2744;
  border: 1.5px solid #1a2744;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #1a2744;
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DiscountSuccess = styled.div`
  margin-top: 0.6rem;
  font-size: 0.88rem;
  color: #2e7d32;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const DiscountError = styled.div`
  margin-top: 0.6rem;
  font-size: 0.88rem;
  color: #c0392b;
`;

const SavingsLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  font-size: 0.9rem;
  color: #2e7d32;
  font-weight: 600;
`;

// Buttons
const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #1a2744 0%, #2c3e6b 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.9rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  letter-spacing: 0.04em;
  transition: all 0.25s;
  margin-top: 1.25rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2c3e6b 0%, #3a4e82 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(26,39,68,0.22);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryBtn = styled.button`
  background: transparent;
  color: #1a2744;
  border: 1.5px solid #1a2744;
  border-radius: 8px;
  padding: 0.7rem 1.5rem;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.75rem;

  &:hover {
    background: #f0ebe2;
  }
`;

// Loading / Error
const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e8e2d9;
  border-top-color: #1a2744;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 2rem auto;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.8);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  gap: 1rem;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #1a2744;
  font-weight: 600;
`;

const ErrorBox = styled.div`
  background: #fff8f5;
  border: 1.5px solid #e07c5a;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  margin-top: 1rem;
  color: #a0341a;
  font-size: 0.95rem;
  line-height: 1.6;
`;

// No-date gate
const NoDateWrapper = styled.div`
  min-height: 100vh;
  background: #f9f7f4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 158px 1.25rem 2rem;
  text-align: center;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const NoDateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.25rem;
`;

const NoDateHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 400;
  color: #1a2744;
  margin: 0 0 0.75rem;
`;

const NoDateBody = styled.p`
  font-size: 1rem;
  color: #555;
  max-width: 420px;
  line-height: 1.7;
  margin: 0 auto;
`;

// Confirmation
const ConfirmIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const RefBox = styled.div`
  background: #f5f0e8;
  border: 1px solid #c9a96e;
  border-radius: 10px;
  padding: 1.25rem;
  text-align: center;
  margin: 1.25rem 0;
`;

const RefLabel = styled.p`
  font-size: 0.82rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #888;
  margin: 0 0 0.35rem;
`;

const RefNumber = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a2744;
  margin: 0;
  letter-spacing: 0.06em;
`;

const ConfirmDetail = styled.p`
  font-size: 0.95rem;
  color: #555;
  margin: 0.5rem 0;
  text-align: center;
`;

// ─── Main Component ───────────────────────────────────────────────────────────
const ExplorerLunchBoxJourney = () => {
  const location = useLocation();

  // URL param — visit date
  const visitDate = parseUrlDate(new URLSearchParams(location.search).get('date'));

  // Step state
  const [step, setStep]                     = useState(STEP_PRODUCTS);
  const [products, setProducts]             = useState([]);
  const [quantities, setQuantities]         = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productError, setProductError]     = useState(null);

  // Customer details
  const [customerDetails, setCustomerDetails] = useState({
    selectedTitle: 1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postcode: '',
    contactable: false,
    agreedToTerms: false,
  });
  const [detailsErrors, setDetailsErrors]   = useState({});

  // Basket / payment
  const [basketId, setBasketId]             = useState(null);
  const [orderId, setOrderId]               = useState(null);
  const [registeredCustomerId, setRegisteredCustomerId] = useState(null);
  const [basketItems, setBasketItems]       = useState([]);
  const [paymentSession, setPaymentSession] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError]     = useState(null);

  // Discount code
  const [discountCode, setDiscountCode]     = useState('');
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [discountSaving, setDiscountSaving] = useState(0);
  const [discountError, setDiscountError]   = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  // Confirmation
  const [bookingReference, setBookingReference] = useState(null);
  const [totalPaid, setTotalPaid]           = useState(0);

  // General busy state
  const [busy, setBusy]                     = useState(false);
  const [busyMessage, setBusyMessage]       = useState('');

  // Prevent double-processing
  const processingRef = useRef(false);

  // ── Load products on mount ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      setProductError(null);
      try {
        const url = `${VENPOS_API_BASE}/WebSaleItem/Get?locationId=${LOCATION_ID}&id=${PAGE_ID}&pageType=WebPage&dateSelectorType=NoDate&selectedDate=`;
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          mode: 'cors',
          credentials: 'omit',
        });
        if (!res.ok) throw new Error(`Failed to load products (${res.status})`);
        const data = await res.json();
        if (data.isError) throw new Error(data.information || 'Failed to load products');
        const items = data.data?.webSaleItems || [];
        setProducts(items);
        const initialQty = {};
        items.forEach(p => { initialQty[p.webSaleItemId] = 0; });
        setQuantities(initialQty);
      } catch (err) {
        setProductError(err.message);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // ── Computed values ─────────────────────────────────────────────────────────
  const selectedItems = products.filter(p => (quantities[p.webSaleItemId] || 0) > 0);
  const totalQuantity = Object.values(quantities).reduce((s, q) => s + q, 0);
  const totalAmount   = products.reduce((sum, p) => sum + (p.price * (quantities[p.webSaleItemId] || 0)), 0);

  // ── Quantity handlers ───────────────────────────────────────────────────────
  const increment = useCallback((id) => {
    setQuantities(q => ({ ...q, [id]: (q[id] || 0) + 1 }));
  }, []);

  const decrement = useCallback((id) => {
    setQuantities(q => ({ ...q, [id]: Math.max(0, (q[id] || 0) - 1) }));
  }, []);

  // ── Step 1 → 2 ─────────────────────────────────────────────────────────────
  const handleProceedToDetails = () => {
    if (totalQuantity === 0) return;
    setStep(STEP_DETAILS);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Customer form handlers ──────────────────────────────────────────────────
  const handleFieldChange = (field, value) => {
    setCustomerDetails(d => ({ ...d, [field]: value }));
    if (detailsErrors[field]) {
      setDetailsErrors(e => { const n = { ...e }; delete n[field]; return n; });
    }
  };

  const validateDetails = () => {
    const errs = {};
    if (!customerDetails.firstName.trim())  errs.firstName = 'First name is required';
    if (!customerDetails.lastName.trim())   errs.lastName  = 'Last name is required';
    if (!customerDetails.email.trim())      errs.email     = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email)) errs.email = 'Please enter a valid email';
    if (!customerDetails.phone.trim())      errs.phone     = 'Phone number is required';
    if (!customerDetails.postcode.trim())   errs.postcode  = 'Billing postcode is required';
    if (!customerDetails.agreedToTerms)     errs.agreedToTerms = 'You must agree to the terms';
    return errs;
  };

  // ── Step 2 → 3: register customer + create basket (no payment init yet) ─────
  const handleProceedToReview = async () => {
    const errs = validateDetails();
    if (Object.keys(errs).length > 0) {
      setDetailsErrors(errs);
      return;
    }

    setBusy(true);
    setBusyMessage('Setting up your order…');
    setPaymentError(null);

    try {
      // 1. Register guest customer
      const regResult = await venposService.registerGuestUser(LOCATION_ID, {
        selectedTitle: customerDetails.selectedTitle,
        firstName: customerDetails.firstName,
        lastName: customerDetails.lastName,
        email: customerDetails.email,
        phone: customerDetails.phone,
        mobile: customerDetails.phone,
        contactable: customerDetails.contactable,
        address: { country: 'GB', postcode: customerDetails.postcode, city: '', line1: '', line2: '', houseNumber: '' },
      });

      const guestCustomerId = regResult?.data?.webCustomerId || null;
      setRegisteredCustomerId(guestCustomerId);

      // 2. Build basket items
      const newBasketItems = products
        .filter(p => (quantities[p.webSaleItemId] || 0) > 0)
        .map(p => ({
          pageType: 'WebPage',
          pageId: PAGE_ID,
          fixedDonationAmount: 0,
          giftAidEligible: false,
          webSaleItemId: p.webSaleItemId,
          quantity: quantities[p.webSaleItemId],
          webSaleItemTypeID: p.webSaleItemTypeID,
          name: p.name,
          price: p.price,
          totalPrice: p.price * quantities[p.webSaleItemId],
          isSysGift: false,
          isSysMemberCard: false,
          selectedDate: visitDate || null,
          eventId: null,
          reservationExpiryDateTime: null,
          eventCapacityUsageId: null,
        }));

      // 3. Load basket
      const basketResult = await venposService.createBasketWithValidation(
        LOCATION_ID,
        newBasketItems,
        { webCustomerId: guestCustomerId },
        null,
        guestCustomerId
      );

      if (basketResult.isError) throw new Error(basketResult.error || 'Failed to create basket');

      const newBasketId = basketResult.data?.basketId;
      const newOrderId  = basketResult.data?.orderId;
      setBasketId(newBasketId);
      setOrderId(newOrderId);
      setBasketItems(newBasketItems);
      setStep(STEP_PAYMENT);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setPaymentError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  };

  // ── Apply discount code ──────────────────────────────────────────────────────
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    setDiscountError(null);

    try {
      const activeBasketId = basketId || orderId;
      const newCode = discountCode.trim().toUpperCase();

      const currentCodes = appliedDiscounts.map(d => d.code || d).filter(Boolean);
      if (currentCodes.some(c => c.toUpperCase() === newCode)) {
        setDiscountError('This discount code has already been applied.');
        setApplyingDiscount(false);
        return;
      }

      const allCodes = [...currentCodes, newCode];

      const result = await venposService.refreshBasketWithDiscounts(
        LOCATION_ID,
        activeBasketId,
        basketItems,
        allCodes,
        registeredCustomerId,
        activeBasketId,
        PAGE_ID
      );

      if (result.isError) {
        setDiscountError('This discount code is not valid. Please check and try again.');
        return;
      }

      const newDiscounts = result.data?.discounts || [];
      const appliedCode  = newDiscounts.find(d => (d.code || '').toUpperCase() === newCode);
      if (!appliedCode) {
        setDiscountError('This discount code could not be applied. It may have expired or not apply to these items.');
        return;
      }

      setAppliedDiscounts(newDiscounts);
      const saving = newDiscounts.reduce((s, d) => s + (d.discountedAmount || 0), 0);
      setDiscountSaving(saving);
      setDiscountCode('');
    } catch (err) {
      setDiscountError('Unable to apply discount code. Please try again.');
    } finally {
      setApplyingDiscount(false);
    }
  };

  // ── Step 3: initialise PaymentSense (called on "Pay Now" click) ─────────────
  const handleInitializePayment = async () => {
    setBusy(true);
    setBusyMessage('Initialising payment…');
    setPaymentError(null);

    try {
      const activeBasketId = basketId || orderId;
      const discountCodes  = appliedDiscounts.map(d => d.code || d).filter(Boolean);
      const finalAmount    = Math.max(0, totalAmount - discountSaving);

      const paymentResult = await venposService.createPaymentRequest(
        LOCATION_ID,
        activeBasketId,
        {
          firstName: customerDetails.firstName,
          lastName:  customerDetails.lastName,
          email:     customerDetails.email,
          phone:     customerDetails.phone,
          mobile:    customerDetails.phone,
          address:   { country: 'GB', postcode: customerDetails.postcode, city: '', line1: '', line2: '', houseNumber: '' },
          webCustomerId: registeredCustomerId,
        },
        finalAmount,
        basketItems,
        visitDate ? new Date(visitDate + 'T12:00:00') : null,
        PAGE_ID,
        {},
        registeredCustomerId,
        discountCodes,
        null,
        null
      );

      if (paymentResult.isError) throw new Error(paymentResult.error || 'Failed to initialise payment');

      setPaymentSession(paymentResult.data || paymentResult);
    } catch (err) {
      setPaymentError(err.message || 'Something went wrong initialising payment. Please try again.');
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  };

  // ── Payment success handler ─────────────────────────────────────────────────
  const handlePaymentSuccess = useCallback(async (paymentResult) => {
    if (processingRef.current) return;
    processingRef.current = true;
    setIsProcessingPayment(true);

    try {
      const paymentToken = paymentResult?.paymentToken || paymentResult?.transactionId || paymentResult?.authCode;
      const activeBasketId = basketId || orderId;

      // 1. Complete payment with Venpos
      let completionResult = null;
      try {
        completionResult = await venposService.completePayment(
          LOCATION_ID,
          activeBasketId,
          paymentToken,
          { ...paymentResult, orderId: activeBasketId },
          PAGE_ID
        );
      } catch (completionErr) {
        // Non-fatal — PaymentSense already took payment, continue
      }

      // 2. Confirm booking (gets booking reference from Venpos)
      let bookingRef = completionResult?.data?.bookingReference || null;
      let confirmedTotal = Math.max(0, totalAmount - discountSaving);

      if (!bookingRef) {
        try {
          const confirmResult = await venposService.confirmBooking(
            activeBasketId,
            LOCATION_ID,
            {
              selectedDate: visitDate ? new Date(visitDate + 'T12:00:00') : new Date(),
              selectedTimeSlot: null,
              selectedTickets: products
                .filter(p => (quantities[p.webSaleItemId] || 0) > 0)
                .map(p => ({
                  pageType: 'WebPage',
                  pageId: PAGE_ID,
                  webSaleItemId: p.webSaleItemId,
                  name: p.name,
                  quantity: quantities[p.webSaleItemId],
                  price: p.price,
                  totalPrice: p.price * quantities[p.webSaleItemId],
                  webSaleItemTypeID: p.webSaleItemTypeID,
                  selectedDate: visitDate || null,
                })),
              pageId: PAGE_ID,
              totalAmount: totalAmount,
              venposBasket: null,
              paymentToken: paymentToken,
              transactionId: paymentResult?.transactionId,
              customerDetails: {
                ...customerDetails,
                webCustomerId: registeredCustomerId,
              },
              isMembershipJourney: false,
            }
          );
          if (!confirmResult.isError && confirmResult.data?.bookingReference) {
            bookingRef = confirmResult.data.bookingReference;
          }
        } catch (confirmErr) {
          // Non-fatal fallback
        }
      }

      // Fallback reference if Venpos didn't return one
      const finalRef = bookingRef || completionResult?.data?.orderId || activeBasketId || `LB-${Date.now()}`;
      setBookingReference(finalRef);
      setTotalPaid(confirmedTotal);

      // 3. Store booking data to WordPress database (tickets / wallet / print)
      try {
        const ticketItems = products
          .filter(p => (quantities[p.webSaleItemId] || 0) > 0)
          .map(p => ({
            webSaleItemId: p.webSaleItemId,
            name: p.name,
            quantity: quantities[p.webSaleItemId],
            price: p.price,
            totalPrice: p.price * quantities[p.webSaleItemId],
            webSaleItemTypeID: p.webSaleItemTypeID,
          }));

        const fullBookingData = {
          bookingReference: finalRef,
          selectedTickets: ticketItems,
          selectedDate: visitDate ? new Date(visitDate + 'T12:00:00').toISOString() : null,
          selectedTimeSlot: null,
          customerDetails: customerDetails,
          totalAmount: confirmedTotal,
          venposBasket: null,
          bookingSummary: {
            pageId: PAGE_ID,
            journeyType: 'ExplorerLunchBox',
            visitDate: visitDate || null,
          },
          isFamilySaver: false,
        };

        await fetch(`${config.apiEndpoint}/custom/v1/store-booking-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fullBookingData),
        });
      } catch (storeErr) {
        console.warn('[ExplorerLunchBox] Failed to store booking data:', storeErr);
      }

      // 4. Store merchandise pickup — shows in Admin → Merchandise tab (same as event-journey add-ons)
      try {
        const merchandiseItems = products
          .filter(p => (quantities[p.webSaleItemId] || 0) > 0)
          .map(p => ({
            name: p.name,
            webSaleItemId: p.webSaleItemId,
            quantity: quantities[p.webSaleItemId],
            price: p.price,
            total: p.price * quantities[p.webSaleItemId],
          }));

        if (merchandiseItems.length > 0) {
          const merchandisePayload = {
            bookingReference: finalRef,
            visitDate: visitDate,
            visitTime: '',
            customerName: `${customerDetails.firstName} ${customerDetails.lastName}`.trim(),
            customerEmail: customerDetails.email,
            customerPhone: customerDetails.phone || '',
            bookingTotal: confirmedTotal,
            merchandiseItems,
          };

          await fetch(`${config.apiEndpoint}/custom/v1/store-merchandise-booking`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(merchandisePayload),
          });
        }
      } catch (mercErr) {
        console.warn('[ExplorerLunchBox] Failed to store merchandise pickup:', mercErr);
      }

      setStep(STEP_CONFIRMATION);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setPaymentError('Your payment was taken but we encountered an issue confirming your booking. Please contact us with your order details.');
    } finally {
      setIsProcessingPayment(false);
      processingRef.current = false;
    }
  }, [basketId, orderId, customerDetails, registeredCustomerId, products, quantities, visitDate, totalAmount, discountSaving]);

  const handlePaymentFailure = useCallback((err) => {
    setPaymentError(typeof err === 'string' ? err : 'Payment failed. Please try again or use a different card.');
    setPaymentSession(null);
    processingRef.current = false;
  }, []);

  const handlePaymentCancel = useCallback(() => {
    setPaymentSession(null);
    setPaymentError(null);
    processingRef.current = false;
  }, []);

  // ── Progress indicator ──────────────────────────────────────────────────────
  const steps = [
    { n: 1, label: 'Select' },
    { n: 2, label: 'Details' },
    { n: 3, label: 'Payment' },
    { n: 4, label: 'Done' },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  // Guard: a valid visit date is required
  if (!visitDate) {
    return (
      <NoDateWrapper>
        <NoDateIcon>🗓️</NoDateIcon>
        <NoDateHeading>A visit date is required</NoDateHeading>
        <NoDateBody>
          To add Explorer Lunch Boxes to your booking, please use the link from your
          confirmation email — it includes your visit date automatically.
          <br /><br />
          If you can't find the email, please{' '}
          <a href="/your-visit" style={{ color: '#1a2744', fontWeight: 600 }}>
            contact us
          </a>{' '}
          and we'll be happy to help.
        </NoDateBody>
      </NoDateWrapper>
    );
  }

  return (
    <Wrapper>
      {/* Loading overlay */}
      {(busy || isProcessingPayment) && (
        <LoadingOverlay>
          <Spinner />
          <LoadingText>{busyMessage || (isProcessingPayment ? 'Confirming your booking…' : 'Please wait…')}</LoadingText>
        </LoadingOverlay>
      )}

      {/* Hero */}
      <HeroStrip>
        <HeroEyebrow>Belvoir Castle</HeroEyebrow>
        <HeroTitle>Explorer Lunch Box</HeroTitle>
        <HeroSub>Themed Sandwich · Snack · Juice Carton</HeroSub>
        {visitDate && <DateBadge>Visit date: {formatVisitDate(visitDate)}</DateBadge>}
      </HeroStrip>

      {/* Progress */}
      {step < STEP_CONFIRMATION && (
        <ProgressBar>
          {steps.map(s => (
            <ProgressStep key={s.n} active={step === s.n} done={step > s.n}>
              <StepCircle active={step === s.n} done={step > s.n}>
                {step > s.n ? '✓' : s.n}
              </StepCircle>
              <StepLabel active={step === s.n} done={step > s.n}>{s.label}</StepLabel>
            </ProgressStep>
          ))}
        </ProgressBar>
      )}

      <Inner>
        {/* ── STEP 1: Products ── */}
        {step === STEP_PRODUCTS && (
          <>
            {loadingProducts && <Spinner />}
            {productError && (
              <ErrorBox>
                Unable to load products: {productError}. Please refresh the page and try again.
              </ErrorBox>
            )}
            {!loadingProducts && !productError && (
              <Card>
                <SectionTitle>Choose Your Lunch Boxes</SectionTitle>
                {visitDate && (
                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                    Adding to your visit on <strong>{formatVisitDate(visitDate)}</strong>
                  </p>
                )}

                {products.map(product => (
                  <ProductRow key={product.webSaleItemId}>
                    <ProductInfo>
                      <ProductName>{product.name}</ProductName>
                      {product.description && <ProductDesc>{product.description}</ProductDesc>}
                      <ProductPrice>{formatPrice(product.price)} each</ProductPrice>
                    </ProductInfo>
                    <QtyControl>
                      <QtyBtn
                        onClick={() => decrement(product.webSaleItemId)}
                        disabled={(quantities[product.webSaleItemId] || 0) === 0}
                        aria-label="Decrease quantity"
                      >
                        −
                      </QtyBtn>
                      <QtyValue>{quantities[product.webSaleItemId] || 0}</QtyValue>
                      <QtyBtn
                        onClick={() => increment(product.webSaleItemId)}
                        disabled={product.maxPurchaseQty > 0 && (quantities[product.webSaleItemId] || 0) >= product.maxPurchaseQty}
                        aria-label="Increase quantity"
                      >
                        +
                      </QtyBtn>
                    </QtyControl>
                  </ProductRow>
                ))}

                {totalQuantity > 0 && (
                  <SummaryLine total style={{ marginTop: '1rem' }}>
                    <span>Total</span>
                    <span>{formatPrice(totalAmount)}</span>
                  </SummaryLine>
                )}

                <PrimaryBtn
                  onClick={handleProceedToDetails}
                  disabled={totalQuantity === 0}
                >
                  Continue to Details →
                </PrimaryBtn>
              </Card>
            )}
          </>
        )}

        {/* ── STEP 2: Customer Details ── */}
        {step === STEP_DETAILS && (
          <Card>
            <SectionTitle>Your Details</SectionTitle>

            <FormGrid>
              <FormField>
                <Label htmlFor="title">Title</Label>
                <Select
                  id="title"
                  value={customerDetails.selectedTitle}
                  onChange={e => handleFieldChange('selectedTitle', Number(e.target.value))}
                >
                  {TITLES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
              </FormField>

              <FormField />

              <FormField>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={customerDetails.firstName}
                  onChange={e => handleFieldChange('firstName', e.target.value)}
                  error={!!detailsErrors.firstName}
                  autoComplete="given-name"
                />
                {detailsErrors.firstName && <FieldError>{detailsErrors.firstName}</FieldError>}
              </FormField>

              <FormField>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={customerDetails.lastName}
                  onChange={e => handleFieldChange('lastName', e.target.value)}
                  error={!!detailsErrors.lastName}
                  autoComplete="family-name"
                />
                {detailsErrors.lastName && <FieldError>{detailsErrors.lastName}</FieldError>}
              </FormField>

              <FormField full>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerDetails.email}
                  onChange={e => handleFieldChange('email', e.target.value)}
                  error={!!detailsErrors.email}
                  autoComplete="email"
                />
                {detailsErrors.email && <FieldError>{detailsErrors.email}</FieldError>}
              </FormField>

              <FormField full>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={customerDetails.phone}
                  onChange={e => handleFieldChange('phone', e.target.value)}
                  error={!!detailsErrors.phone}
                  autoComplete="tel"
                />
                {detailsErrors.phone && <FieldError>{detailsErrors.phone}</FieldError>}
              </FormField>

              <FormField>
                <Label htmlFor="postcode">Billing Postcode *</Label>
                <Input
                  id="postcode"
                  type="text"
                  value={customerDetails.postcode}
                  onChange={e => handleFieldChange('postcode', e.target.value.toUpperCase())}
                  error={!!detailsErrors.postcode}
                  autoComplete="postal-code"
                  placeholder="e.g. NG32 1PE"
                />
                {detailsErrors.postcode && <FieldError>{detailsErrors.postcode}</FieldError>}
              </FormField>

              <FormField />

              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={customerDetails.contactable}
                  onChange={e => handleFieldChange('contactable', e.target.checked)}
                />
                <span>
                  I'd like to receive news and special offers from Belvoir Castle by email.
                </span>
              </CheckboxRow>

              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={customerDetails.agreedToTerms}
                  onChange={e => handleFieldChange('agreedToTerms', e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1a2744' }}>
                    Terms &amp; Conditions
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1a2744' }}>
                    Privacy Policy
                  </a>. *
                </span>
              </CheckboxRow>
              {detailsErrors.agreedToTerms && (
                <FieldError style={{ gridColumn: '1 / -1' }}>{detailsErrors.agreedToTerms}</FieldError>
              )}
            </FormGrid>

            {paymentError && <ErrorBox>{paymentError}</ErrorBox>}

            <PrimaryBtn onClick={handleProceedToReview} disabled={busy}>
              Review Order →
            </PrimaryBtn>
            <SecondaryBtn onClick={() => { setStep(STEP_PRODUCTS); setPaymentError(null); }}>
              ← Back
            </SecondaryBtn>
          </Card>
        )}

        {/* ── STEP 3: Review + Discount + Payment ── */}
        {step === STEP_PAYMENT && (
          <>
            {/* Order summary + discount */}
            <Card>
              <SectionTitle>Order Summary</SectionTitle>
              {selectedItems.map(p => (
                <SummaryLine key={p.webSaleItemId}>
                  <span>{p.name} × {quantities[p.webSaleItemId]}</span>
                  <span>{formatPrice(p.price * quantities[p.webSaleItemId])}</span>
                </SummaryLine>
              ))}

              {discountSaving > 0 && (
                <SavingsLine>
                  <span>Discount saving</span>
                  <span>− {formatPrice(discountSaving)}</span>
                </SavingsLine>
              )}

              <SummaryLine total>
                <span>Total to pay</span>
                <span>{formatPrice(Math.max(0, totalAmount - discountSaving))}</span>
              </SummaryLine>

              {visitDate && (
                <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.75rem', marginBottom: 0 }}>
                  Visit date: {formatVisitDate(visitDate)}
                </p>
              )}

              {/* Discount code — only show before payment session is active */}
              {!paymentSession && (
                <>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #f0ebe2' }}>
                    <Label as="p" style={{ marginBottom: '0.5rem' }}>Discount Code</Label>
                    <DiscountRow>
                      <DiscountInput
                        type="text"
                        value={discountCode}
                        onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(null); }}
                        placeholder="Enter code"
                        onKeyDown={e => e.key === 'Enter' && handleApplyDiscount()}
                      />
                      <ApplyBtn onClick={handleApplyDiscount} disabled={applyingDiscount || !discountCode.trim()}>
                        {applyingDiscount ? 'Applying…' : 'Apply'}
                      </ApplyBtn>
                    </DiscountRow>
                    {discountError && <DiscountError>{discountError}</DiscountError>}
                    {appliedDiscounts.length > 0 && (
                      <DiscountSuccess>
                        ✓ Discount applied — you're saving {formatPrice(discountSaving)}
                      </DiscountSuccess>
                    )}
                  </div>
                </>
              )}
            </Card>

            {/* Payment form */}
            <Card>
              <SectionTitle>Card Payment</SectionTitle>
              {paymentError && <ErrorBox>{paymentError}</ErrorBox>}

              {paymentSession ? (
                <PaymentSenseConnectE
                  paymentSession={paymentSession}
                  onPaymentProcessing={() => setIsProcessingPayment(true)}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentFailure={handlePaymentFailure}
                  onPaymentCancel={handlePaymentCancel}
                />
              ) : (
                <PrimaryBtn onClick={handleInitializePayment} disabled={busy || applyingDiscount}>
                  Proceed to Pay {formatPrice(Math.max(0, totalAmount - discountSaving))} →
                </PrimaryBtn>
              )}

              {!isProcessingPayment && !paymentSession && (
                <SecondaryBtn
                  onClick={() => {
                    setStep(STEP_DETAILS);
                    setPaymentError(null);
                  }}
                >
                  ← Back to Details
                </SecondaryBtn>
              )}
              {!isProcessingPayment && paymentSession && (
                <SecondaryBtn
                  onClick={() => {
                    setPaymentSession(null);
                    setPaymentError(null);
                  }}
                >
                  ← Back to Review
                </SecondaryBtn>
              )}
            </Card>
          </>
        )}

        {/* ── STEP 4: Confirmation ── */}
        {step === STEP_CONFIRMATION && (
          <Card>
            <ConfirmIcon>🎉</ConfirmIcon>
            <SectionTitle style={{ textAlign: 'center', borderBottom: 'none', marginBottom: '0.5rem' }}>
              Booking Confirmed!
            </SectionTitle>
            <ConfirmDetail>
              Thank you, <strong>{customerDetails.firstName}</strong>! Your Explorer Lunch{' '}
              {totalQuantity === 1 ? 'Box has' : 'Boxes have'} been booked.
            </ConfirmDetail>

            <RefBox>
              <RefLabel>Booking Reference</RefLabel>
              <RefNumber>{bookingReference}</RefNumber>
            </RefBox>

            <ConfirmDetail>
              A confirmation has been sent to <strong>{customerDetails.email}</strong>.
            </ConfirmDetail>

            {visitDate && (
              <ConfirmDetail>
                Your lunch {totalQuantity === 1 ? 'box' : 'boxes'} will be ready on{' '}
                <strong>{formatVisitDate(visitDate)}</strong>.
              </ConfirmDetail>
            )}

            <ConfirmDetail style={{ marginTop: '0.5rem' }}>
              <strong>Total paid: {formatPrice(totalPaid)}</strong>
            </ConfirmDetail>

            <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <SummaryLine style={{ background: '#f9f7f4', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                <span style={{ fontSize: '0.88rem', color: '#555' }}>Items booked:</span>
                <span style={{ fontWeight: 600, color: '#1a2744' }}>
                  {selectedItems.map(p => `${quantities[p.webSaleItemId]}× ${p.name}`).join(', ')}
                </span>
              </SummaryLine>
            </div>

            <PrimaryBtn onClick={() => window.location.href = '/'} style={{ marginTop: '2rem' }}>
              Return to Belvoir Castle
            </PrimaryBtn>
          </Card>
        )}
      </Inner>
    </Wrapper>
  );
};

export default ExplorerLunchBoxJourney;
